import React from 'react';
import t from 'tcomb-form';
import moment from 'moment';

import baseFormFactory from './BaseForm';

import imgResolver from '../utils/img-resolver';
import {FormImage, FormFile, FileUpload, ImageUpload} from './FileUpload';
import GoogleLocationInput from '../components/Form_widgets/GoogleLocationInput';
import PhoneFieldComponent from '../components/PhoneFieldComponent';
import {DateRangePickerToggle} from '../components/DateRangePickerModular';
import ReactDatePicker from 'react-date-picker';

import {ValidatonError} from '../resources/GenericResource';

const LanguageName = t.enums({
    en: 'English',
    et: 'Estonian',
    es: 'Spanish',
    pt: 'Portuguese',
    it: 'Italian',
    pl: 'Polish',
    ru: 'Russian',
    cn: 'Mandarin',
    jp: 'Japanese',
    in: 'Hindi',
    fr: 'French',
    ar: 'Arabic',
    de: 'German',
    fi: 'Finnish'
});

const Proficiency = t.enums({
    fluent: 'Fluent',
    professional: 'Professional',
    poor: 'Poor'
});

const Language = t.struct({
    name: t.maybe(LanguageName),
    proficiency: t.maybe(Proficiency)
});

const Certificate = t.struct({
    file: t.maybe(FormFile),
    title: t.maybe(t.Str),
    date: t.maybe(t.Str),
    description: t.maybe(t.Str)
});

const Sex = t.enums({
    m: 'Male',
    f: 'Female'
});

function getMyProfileFormModel() {
    const fields = {
        profile_picture: t.maybe(FormImage),
        address: t.Str,
        phone: t.Str,
        birth_date: t.Str,
        national_insurance_number: t.Str,
        bio: t.Str,
        languages: t.maybe(t.list(Language)),
        certificates: t.maybe(t.list(Certificate)),
        sex:  Sex,
        country: t.maybe(t.Str),
        city: t.Str,
        zip: t.Str,
        street: t.Str,
        lat: t.maybe(t.Num),
        lng: t.maybe(t.Num),
        bank_account_holder: t.Str,
        sort_code: t.Str,
        iban: t.Str
    };

    if (!DJ_CONST.LOCALE_CONF.insurance) {
        fields.national_insurance_number = t.maybe(fields.national_insurance_number);
    }

    if (!DJ_CONST.LOCALE_CONF.sort_code) {
        fields.sort_code = t.maybe(fields.sort_code);
    }

    return t.struct(fields);
}

const CompleteProfileMyProfileFormModel = getMyProfileFormModel();

function languageLayout(locals) {
    const key = `${locals.path[0]}_${locals.path[1]}`;
    const error = locals.error && locals.error['languages_generic'] && locals.path[1] == 0 ? "Please choose a language and a proficiency." :
        locals.error && Object.keys(locals.error).filter(x => x.indexOf(key) > -1).length ? 'Fill in missing field data for this row.' : null;
    return (
        <div>

            <div className={"grid-row repeater-field-row language " + (error ? "has-error" : "")}>
                <label className="form-control form-control-text language-name select-wrapper grid-col-half">
                    <h3 className="label-title">Language</h3>
                    {locals.inputs.name}
                    <div className="error-block">{error}</div>
                </label>
                <label className="form-control form-control-text language-proficiency select-wrapper grid-col-half">
                    <h3 className="label-title">Proficiency</h3>
                    {locals.inputs.proficiency}
                </label>
            </div>
        </div>
    );
}

function certificateLayout(locals) {
    const key = `${locals.path[0]}_${locals.path[1]}`;
    const error = locals.error && Object.keys(locals.error).filter(x => x.indexOf(key) > -1).length ? 'Fill in missing field data for this row.' : null;
    return (
        <div className={"row certificate " + (error ? "has-error" : "")}>
            <label className="form-control form-control-text">
                <div>
                    <div className="inline-input">
                        <h3 className="label-title">Title</h3>
                        {locals.inputs.title}
                    </div>
                    {locals.inputs.date}
                    <div className="inline-input">
                        <h3 className="label-title">Short description</h3>
                        {locals.inputs.description}
                    </div>
                </div>
                <h3 className="label-title">Certificate</h3>
                {locals.inputs.file}
            </label>
            {error ? <div className="error-block">{error}</div> : ''}
        </div>
    );
}

function locationTemplate(locals){
    return <GoogleLocationInput
        callBack={this.handleAddressChange.bind(this)}
        reference={'locationInput'}
        onChange={this.onChange}
        onBlur={this.onBlur.bind(this)}
        types={'address'}
        {...locals}
        placeholder={''} />
}

function certificateDateTemplate(locals) {
    const value = locals.value ? moment(locals.value).format('DD.MM.YYYY') : '';
    const cur_path = `${locals.path[0]}_${locals.path[1]}`;
    const onDateChange = (datestring, d) => {
        locals.onChange(d.format('YYYY-MM-YY'));
        this.toggleCertificateDatePicker(cur_path);
    }.bind(this);
    return (
        <div className="inline-input">
            <h3 className="label-title">Date</h3>
            <div className="Select-control" onClick={this.toggleCertificateDatePicker.bind(this, cur_path)}>
                <div className="time-period" >
                    <DateRangePickerToggle dateText={value}/>
                </div>
            </div>
            <div className={"datepicker-wrapper " + (this.state[cur_path] ? "active" : "")}>
                <ReactDatePicker
                    defaultDate={Date.now()}
                    todayText="This month"
                    gotoSelectedText="Selected month"
                    hideFooter
                    defaultView="decade"
                    maxDate={Date.now()}
                    onChange={onDateChange} />
            </div>
        </div>
    );
}

function birthdayTemplate(locals) {
    const value = locals.value ? moment(locals.value).format('DD.MM.YYYY') : '';
    return (
        <label className={"form-control form-control-text birthday " + (locals.error ? "has-error" : "")}>
            <h3 className="label-title">Birthday</h3>
            <div className="Select" onClick={this.toggleBirthDatePicker.bind(this)}>
                <div className="Select-control">
                    <div className="time-period" >
                        <DateRangePickerToggle dateText={value}/>
                    </div>
                </div>
            </div>
            <span className="error-msg">{locals.error}</span>

            <div  className={"datepicker-wrapper " + (this.state.showDatePicker ? "active" : "")}>
                <ReactDatePicker
                    defaultDate={Date.now()}
                    todayText="This month"
                    gotoSelectedText="Selected month"
                    hideFooter
                    defaultView="decade"
                    maxDate={Date.now()}
                        {...locals} />
            </div>
        </label>
    );
}

function phoneInputTemplate(locals){
    return (
        <PhoneFieldComponent {...locals}/>
    );
}


class FormLayout extends React.Component {
    constructor(props) {
        super(props);
    }

    render() {
        const {inputs, value} = this.props;

        return (
            <div className="settings-form">
                <div className="section-content media">
                    <div className="left">
                        <h3 className="label-title">Profile picture</h3>
                        <div className="upload-container">
                            <div className="form-control form-control-upload">
                                <div className="form-control-upload-content">
                                </div>
                                {inputs.profile_picture}
                            </div>
                            <p className="recommended-size">Recommended size: 250x250px</p>
                        </div>
                    </div>
                    <div className="form-cols body">
                        <div className="col">

                            <label className="form-control form-control-text">
                                {inputs.address}
                                {inputs.lat}
                                {inputs.lng}
                                {inputs.country}
                                {inputs.city}
                                {inputs.street}
                                {inputs.zip}
                                <span className="error-msg"></span>
                            </label>
                            {inputs.birth_date}
                        </div>
                        <div className="col">
                            <label className="form-control form-control-text">
                                <h3 className="label-title">Phone</h3>
                                {inputs.phone}
                            </label>
                        </div>
                    </div>
                </div>
                <div className="row description">
                    <h3 className="label-title">Bio</h3>
                    {inputs.bio}
                </div>
                <label className={"gender " + (inputs.sex.props.options.error ? "has-error" : "")}>
                    <h3 className="label-title">Gender</h3>
                    <div className={"form-control-select"}>
                        {inputs.sex}
                    </div>
                </label>
                <div className="row repeater-field language">
                    {inputs.languages}
                </div>
                <div className="row repeater-field repeater-field-files certificates">
                    <div className="label-title">Certificates</div>
                    <p className="label-info">Upload certificates and/or reference letters here, so that companies can verify your skills.</p>
                    {inputs.certificates}
                </div>
                <div className="row payment-details">
                    <div className="col">
                        <label className="form-control form-control-text">
                            <h3 className="label-title">National Insurance number (hidden)</h3>
                            {inputs.national_insurance_number}
                            <span className="error-msg">{inputs.national_insurance_number.error}</span>
                        </label>
                        <label className="form-control form-control-text margin-left">
                            <h3 className="label-title">Bank</h3>
                            {inputs.bank_account_holder}
                            <span className="error-msg">{inputs.bank_account_holder.error}</span>
                        </label>
                    </div>
                    <div className="col">
                        <label className="form-control form-control-text">
                            <h3 className="label-title">Sort code</h3>
                            {inputs.sort_code}
                            <span className="error-msg">{inputs.sort_code.error}</span>
                        </label>
                        <label className="form-control form-control-text margin-left">
                            <h3 className="label-title">Bank account</h3>
                            {inputs.iban}
                            <span className="error-msg">{inputs.iban.error}</span>
                        </label>
                    </div>
                </div>
            </div>
        );
    }
}

function makeLayout(locals) {
    return <FormLayout {...locals}/>
}

const Form = baseFormFactory(CompleteProfileMyProfileFormModel, true, true);

class CompleteProfileMyProfileForm extends Form {
    constructor(props) {
        super(props);
        let isValid = true;
        let test = Object.values(props.initialFormData);
        for (var i = 0; i < test.length; i++) {
            if (typeof test[i] === 'undefined' || test[i] === '') {
                isValid = false;
                break;
            }
        }

        if (typeof props.initialFormData.languages === 'undefined' || props.initialFormData.languages.length == 0) {
            props.initialFormData.languages = [{name: "", proficiency: ""}];
        }

        const value = props.initialFormData;

        this.state = {
            value,
            isValid,
            showDatePicker: false,
            previousLength: 0,
            showBioInfo: true
        };
    }

    cleanLanguages(values) {
        return values.languages.filter((item) => {
            return (item.name !== '' && item.name !== null && item.proficiency !== '' && item.proficiency !== null);
        });
    }

    validateMissingLanguagesData(values) {
        const result = {};
        values.languages.forEach((item, index) => {
            const checks = [
                item.name == '' || item.name == null,
                item.proficiency == '' || item.proficiency == null
            ];
            if (checks.some(x => x == true) && checks.some(x => x == false)) {
                result[`languages_${index}`] = 'Incomplete data';
            }
        });
        return result;
    }

    validateMissingCertificatesData(values) {
        const result = {};
        let index = 0;
        if ( values.certificates !== null ) {
            values.certificates.map(item => {
                const checks = [
                    item.title == '' || item.title == null,
                    item.description == '' || item.description == null,
                    item.date == '' || item.date == null
                ];
                if (checks.some(x => x == true) && checks.some(x => x == false)) {
                    result[`certificates_${index}`] = 'Incomplete data';
                }
                index++;
            });
        }
        return result;
    }

    getValue() {
        let values = this.refs.form.getValue();
        if (values !== null) {
            values = _.extend({}, values);
            values.languages = this.cleanLanguages(values);

            if (values.languages.length < 1) {
                values = null;
            }
        }

        return values;
    }

    postValidate(state) {
        const value = this.refs.form.getValue();
        if (state.isValid) {
            const missingLanguageDataErrors = this.validateMissingLanguagesData(value);
            const missingCertificateDataErrors = this.validateMissingCertificatesData(value);
            if (DJ_CONST.LOCALE_CONF.sort_code && value.sort_code.length < 8) {
                state.isValid = false;
                state.errors = _.merge({}, state.errors, {'sort_code': 'Sort code is incomplete'});
            }
            if (!value.languages || value.languages.length < 1 || this.cleanLanguages(value).length < 1) {
                state.isValid = false;
                state.errors = _.merge({}, state.errors, {'languages_generic': 'blabla'});
            }
            if (Object.keys(missingLanguageDataErrors).length > 0) {
                state.isValid = false;
                state.errors = _.merge({}, state.errors, missingLanguageDataErrors);
            }
            if (Object.keys(missingCertificateDataErrors).length > 0) {
                state.isValid = false;
                state.errors = _.merge({}, state.errors, missingCertificateDataErrors);
            }
            if (DJ_CONST.LOCALE_CONF.national_insurance_number && (!value.national_insurance_number || value.national_insurance_number.length != 9 || !value.national_insurance_number.match(/^[A-Z]{2}[0-9]{6}[A-Z]$/g))) {
                state.isValid = false;
                state.errors = _.merge({}, state.errors, {'national_insurance_number': 'Please check that this is in the format of: AA000000A'});
            }
        }
        return state;
    }

    onChange(value, path) {
        let previousLength = this.state.previousLength;

        if (path.indexOf('sort_code') > -1) {
            value.sort_code = value.sort_code.replace(/[^0-9]+/g, '');
            if (value.sort_code.match(/.{1,2}/g)) {
                value.sort_code = value.sort_code.match(/.{1,2}/g).join('-');
            }

            if (value.sort_code.length > 8) {
                value.sort_code = value.sort_code.slice(0, 8);
            }
        }

        if (DJ_CONST.LOCALE_CONF.validate_bank && path.indexOf('iban') > -1) {
            if (value.iban.length >= 8) {
                value.iban =  value.iban.slice(0,8);
            }
            value.iban = value.iban.replace(/[^0-9\/]+/g, '');
        }

        if (path.indexOf('birth_date') > -1) {
            this.toggleBirthDatePicker();
        }

        if (path.indexOf('languages') > -1) {
            if ((path.indexOf('name') > -1 || path.indexOf('proficiency') > -1) && value.languages[path[1]]) {
                const name = value.languages[path[1]].name;
                const proficiency = value.languages[path[1]].proficiency;
                value.languages.forEach((language, index) => {
                    if (language && path[1] != index && language.name == name && language.proficiency == proficiency) {
                        language.name = '';
                        language.proficiency = '';

                    }
                });
            }
        }

        this.setState({previousLength});
        super.onChange(value);
    }

    onBlur(path) {
        const value = this.state.value;
        if (path.indexOf('address') > -1) {
            value.lat = 51.500152;
            value.lng =  -0.126236;
            value.country = '';
            value.city = '';
            value.street = '';
            value.zip = '';
            value.address = null;
        }

        this.setState({value});
    }

    handleAddressChange(place) {
        // TODO: There is a small race condition here - we reset all the address fields on blur event and then after we
        // get response from Google we check the existence of city. If city existed in earlier state value and we got
        // response from Google before the blur event fired then the city check would give us wrong results AND we would
        // empty the fields after adding valid data to state.
        const value = this.state.value;
        value.lat = String(place.geometry.location.lat()).substr(0, 12);
        value.lng = String(place.geometry.location.lng()).substr(0, 12);
        value.country = _.result(_.find(place.address_components, comp => {
            return _.includes(comp.types, "country");
        }), 'short_name');
        value.city = _.result(_.find(place.address_components, comp => {
            return _.includes(comp.types, "locality");
        }), 'long_name');
        // Fall back to sublocality if the results didn't include locality.
        if (!value.city) {
            value.city = _.result(_.find(place.address_components, comp => {
                return _.includes(comp.types, "sublocality");
            }), 'long_name');
        }
        value.street = _.result(_.find(place.address_components, comp => {
            return _.includes(comp.types, "route");
        }), 'long_name');
        const street_number = _.result(_.find(place.address_components, comp => {
            return _.includes(comp.types, "street_number");
        }), 'long_name');
        if (typeof street_number !== 'undefined' && street_number !== '') {
            value.street = street_number + ' ' + value.street;
        }
        value.zip = _.result(_.find(place.address_components, comp => {
            return _.includes(comp.types, "postal_code");
        }), 'long_name');
        value.address = place.formatted_address;
        this.setState({value});
    }

    toggleBirthDatePicker() {
        const newState = {}
        newState.showDatePicker = !this.state.showDatePicker;
        const activePicker = Object.keys(this.state).filter(key => { return key.startsWith('certificate') && this.state[key] == true});
        if (activePicker.length) {
            newState[activePicker] = false;
        }
        this.setState(newState);
    }

    toggleCertificateDatePicker(path) {
        const newState = {};
        const activePicker = Object.keys(this.state).filter(key => { return key.startsWith('certificate') && this.state[key] == true});
        if (activePicker.length) {
            newState[activePicker] = false;
        }
        newState.showDatePicker = false;
        newState[path] = !this.state[path];
        this.setState(newState);
    }

    closeInfo() {
        this.setState({showBioInfo: false});
    }

    renderBioInfo() {
        if (this.state.showBioInfo) {
            return (
                <div className="description-info">
                    <img className="info-icon" src={require('../styles/images/icon_info.svg')} alt="Gigpanel" />
                    <div className="info-text">
                        <h3 className="label-title">Need help writing a description?</h3>
                        <p className="info-description">Please introduce yourself briefly including your personal qualities, key skills and work experience</p>
                    </div>
                    <button className="close" onClick={this.closeInfo.bind(this)}><span>&times;</span></button>
                </div>
            )
        }
    }


    getOptions() {
        const errors = this.props.errors;
        return {
            auto: 'none',

            template: makeLayout,

            fields: {
                profile_picture: {
                    type: 'file',
                    factory: ImageUpload,
                    hasError: !!errors
                },

                address: {
                    type: 'text',
                    template: locationTemplate.bind(this),
                    hasError: !!errors,
                    error: errors && errors['address'] ? "Please enter an address." : errors['zip'] || errors['street']
                            || errors['city'] ? "Please choose a more specific address." : null
                },

                birth_date: {
                    type: 'text',
                    template: birthdayTemplate.bind(this),
                    hasError: !!errors,
                    error: errors && errors['birth_date'] ? "Please select a date." : null
                },

                national_insurance_number: {
                    type: 'text',
                    hasError: !!errors,
                    error: errors && errors['national_insurance_number'] ? errors['national_insurance_number'] : null
                },

                sex: {
                    factory: t.form.Select,
                    hasError: !!errors,
                    error: errors && errors['sex'] ? "This field is required." : null,
                    attrs: {
                        className: 'gender'
                    }
                },

                bio: {
                    template: (locals) => {
                        let error = errors && errors['bio'] ? <span className="error-block">Please write a description</span> : null;
                        return (
                            <div className={"form-group form-group-depth-1 " + (errors && errors['bio'] ? "has-error" : null)}>
                                <textarea rows="8" value={locals.value} onChange={evt => locals.onChange(evt.target.value, ['bio'])}>
                                </textarea>
                                {!error ? this.renderBioInfo() : null}
                                {error}
                            </div>
                        );
                    }
                },

                languages: {
                    disableOrder: true,
                    item: {
                        template: languageLayout,
                        error: errors
                    },
                    i18n: {
                        add: 'Add a new language'
                    }

                },

                phone: {
                    template: phoneInputTemplate,
                    hasError: !!errors,
                    error: errors &&
                            (errors['phone'] ? "Please enter a valid phone number." : null) ||
                            (Object.keys(errors).filter(x => x.indexOf('phone') > -1).length ? "Enter a valid phone number." : null)
                },

                certificates: {
                    disableOrder: true,
                    item: {
                        template: certificateLayout.bind(this),
                        error: errors,
                        fields: {
                            file: {
                                type: 'file',
                                factory: FileUpload,
                                attrs: {
                                    className: 'certificate-input'
                                }
                            },
                            title: {
                                type: 'text'
                            },
                            date: {
                                type: 'text',
                                template: certificateDateTemplate.bind(this)
                            },
                            description: {
                                type: 'text'
                            }
                        }
                    },
                    i18n: {
                        add: 'Add a certificate'
                    }
                },

                country: {
                    type: 'hidden'
                },

                city: {
                    type: 'hidden'
                },

                street: {
                    type: 'hidden'
                },

                zip: {
                    type: 'hidden'
                },

                lat: {
                    type: 'hidden'
                },

                lng: {
                    type: 'hidden'
                },
                bank_account_holder: {
                    type: 'text',
                    hasError: !!errors,
                    error: errors && errors['bank_account_holder'] ? "Please add a bank.." : null
                },
                sort_code: {
                    type: 'text',
                    hasError: !!errors,
                    error: errors && errors['sort_code'] ? "Please add a sort code." : null
                },
                iban: {
                    type: 'text',
                    hasError: !!errors,
                    error: errors && errors['iban'] ? "Please add an bank account." : null
                }
            }
        };
    }

    render() {
        return (
            <div>
                <t.form.Form ref="form" value={this.state.value} type={this.getModel()}
                             options={this.buildOptions()} onChange={this.onChange.bind(this)}/>
            </div>
        );
    }
}

CompleteProfileMyProfileForm.propTypes = {
    initialFormData: React.PropTypes.object.isRequired
};


export default CompleteProfileMyProfileForm;



/** WEBPACK FOOTER **
 ** ./app/src/forms/CompleteProfileMyProfileForm.js
 **/